// =============================================================================
// Portal Billing hub — Subscription / Wallet / Payment methods / Payments
// =============================================================================
// Single billing surface replacing the four standalone pages. A `?tab=`
// URL parameter selects the active panel; the legacy routes (/subscription,
// /wallet, /payment-methods, /payments) survive as redirects into this hub
// so notification links, e-mail CTAs and Stripe return URLs keep working.
//
// Tab visibility is permission-gated with the same requirements the old
// standalone routes enforced (hasAnyPermission — `:manage` wildcard works).
// The panels themselves are the unchanged page components, so behavior
// (queries, Stripe redirects, filters) is identical to the old pages.
// =============================================================================

import { useMemo } from 'react';
import { useSearchParams } from 'react-router';
import { hasAnyPermission } from '@tkpod/shared';
import { useAuth } from '@/hooks/use-auth';
import { PortalSubscriptionPage } from './PortalSubscriptionPage';
import { PortalWalletPage } from './PortalWalletPage';
import { PortalPaymentMethodsPage } from './PortalPaymentMethodsPage';
import { PortalPaymentsPage } from './PortalPaymentsPage';

export type BillingTab = 'subscription' | 'wallet' | 'payment-methods' | 'payments';

interface TabDef {
  id: BillingTab;
  label: string;
  requiredPermissions: string[];
  element: React.ReactNode;
}

const ALL_TABS: TabDef[] = [
  {
    id: 'subscription',
    label: 'Subscription',
    requiredPermissions: ['billing:read', 'billing:manage'],
    element: <PortalSubscriptionPage />,
  },
  {
    id: 'wallet',
    label: 'Wallet',
    requiredPermissions: ['wallet:read', 'wallet:manage', 'wallet:top_up'],
    element: <PortalWalletPage />,
  },
  {
    id: 'payment-methods',
    label: 'Payment methods',
    requiredPermissions: ['wallet:read', 'wallet:manage'],
    element: <PortalPaymentMethodsPage />,
  },
  {
    id: 'payments',
    label: 'Payments',
    requiredPermissions: ['invoice:read', 'invoice:manage'],
    element: <PortalPaymentsPage />,
  },
];

/**
 * Union of all tab permissions — used by the /billing route guard. A user
 * with ANY of these can open the hub; individual tabs are then filtered.
 */
export const BILLING_PAGE_PERMISSIONS = Array.from(
  new Set(ALL_TABS.flatMap((t) => t.requiredPermissions)),
);

export function PortalBillingPage() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const permissions = user?.currentOrganization?.permissions ?? [];
  const visibleTabs = useMemo(
    () =>
      ALL_TABS.filter((t) => hasAnyPermission(permissions, t.requiredPermissions)),
    // permissions is a new array reference each render via optional chain;
    // key on its serialized value to keep the memo stable.
    [permissions.join(',')],
  );

  const requestedTab = searchParams.get('tab');
  const activeTab =
    visibleTabs.find((t) => t.id === requestedTab) ?? visibleTabs[0] ?? null;

  const setTab = (next: BillingTab) => {
    if (next === activeTab?.id) return;
    // Tab-scoped params (pagination, filters, Stripe statuses) must not
    // leak between panels — reset everything except the tab itself.
    setSearchParams({ tab: next });
  };

  if (!activeTab) {
    // Route guard already requires at least one billing permission, so this
    // only renders in edge cases (permissions revoked mid-session).
    return (
      <div className="py-12 text-center text-sm text-text-secondary">
        You do not have access to any billing sections.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {visibleTabs.length > 1 ? (
        <div
          role="tablist"
          aria-label="Billing sections"
          className="flex flex-wrap gap-1 border-b border-border"
        >
          {visibleTabs.map((tab) => {
            const isActive = tab.id === activeTab.id;
            return (
              <button
                key={tab.id}
                role="tab"
                type="button"
                aria-selected={isActive}
                onClick={() => setTab(tab.id)}
                className={
                  'whitespace-nowrap border-b-2 px-3 py-2.5 text-sm font-medium transition-colors ' +
                  (isActive
                    ? 'border-brand-600 text-brand-700'
                    : 'border-transparent text-text-secondary hover:text-text-primary')
                }
              >
                {tab.label}
              </button>
            );
          })}
        </div>
      ) : null}

      <div role="tabpanel" aria-label={activeTab.label}>
        {activeTab.element}
      </div>
    </div>
  );
}
